// Dashboard page — T058 + T064 + T003-filter
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import MonitorCard from '../components/MonitorCard';
import FilterBar from '../components/FilterBar';
import StatusBadge from '../components/StatusBadge';
import Pagination from '../components/Pagination';
import { getMonitors, toggleMute, createMonitor, type Monitor, type PaginationInfo, type StatusCounts, type FilterOptions } from '../services/api';

const AUTO_REFRESH_MS = 60_000;

export default function Dashboard() {
  const { isAdmin } = useAuth();
  const [monitors, setMonitors] = useState<Monitor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [pagination, setPagination] = useState<PaginationInfo>({ page: 1, limit: 50, total: 0, totalPages: 1 });
  const [statusCounts, setStatusCounts] = useState<StatusCounts>({ UP: 0, DEGRADED: 0, DOWN: 0 });
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({ providers: [], accounts: [], clients: [] });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // Filters — persist in sessionStorage
  const [filters, setFilters] = useState(() => {
    try {
      const saved = sessionStorage.getItem('cm_filters');
      return saved ? JSON.parse(saved) : { status: [], hostingProvider: '', providerAccount: '', client: '', name: '', dnsMutated: '' };
    } catch {
      return { status: [], hostingProvider: '', providerAccount: '', client: '', name: '', dnsMutated: '' };
    }
  });

  // Debounced name filter — 300 ms delay so the list doesn't flicker on every keystroke
  const [debouncedName, setDebouncedName] = useState(() => (filters.name ?? '').trim());
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedName((filters.name ?? '').trim()), 300);
    return () => clearTimeout(timer);
  }, [filters.name]);

  const fetchMonitors = useCallback(async () => {
    try {
      // When a name filter is active, fetch all monitors so client-side filtering
      // can search across the full set — not just the current page.
      const nameActive = (filters.name ?? '').trim().length > 0;
      const params: Record<string, string> = {
        page: nameActive ? '1' : String(currentPage),
        limit: nameActive ? '1000' : String(itemsPerPage),
      };
      if (filters.status.length > 0) params.status = filters.status.join(',');
      if (filters.hostingProvider) params.hostingProvider = filters.hostingProvider;
      if (filters.providerAccount) params.providerAccount = filters.providerAccount;
      if (filters.client) params.client = filters.client;
      if (filters.dnsMutated) params.dnsMutated = filters.dnsMutated;

      const result = await getMonitors(params);
      setMonitors(result.data || []);
      setPagination(result.pagination);
      if (result.statusCounts) setStatusCounts(result.statusCounts);
      if (result.filterOptions) setFilterOptions(result.filterOptions);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to load monitors');
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage, itemsPerPage]);

  useEffect(() => {
    fetchMonitors();
    const interval = setInterval(fetchMonitors, AUTO_REFRESH_MS);
    return () => clearInterval(interval);
  }, [fetchMonitors]);

  useEffect(() => {
    sessionStorage.setItem('cm_filters', JSON.stringify(filters));
    // Reset to page 1 when filters change
    setCurrentPage(1);
  }, [filters]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLimitChange = (limit: number) => {
    setItemsPerPage(limit);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleToggleMute = async (id: string, muted: boolean) => {
    try {
      await toggleMute(id, muted);
      fetchMonitors();
    } catch (err: any) {
      setError(err.message || 'Failed to toggle mute');
    }
  };

  // Extract unique values for filter dropdowns — use API-provided filterOptions (total across ALL matching monitors)
  // Amendment 2026-03-02: fixes filter dropdowns only showing values from current page
  const providers = filterOptions.providers;
  const accounts = filterOptions.accounts;
  const clients = filterOptions.clients;

  // Client-side name filter — applied on top of the server-filtered monitors array
  const visibleMonitors = useMemo(() => {
    if (!debouncedName) return monitors;
    const q = debouncedName.toLowerCase();
    return monitors.filter((m) => m.name.toLowerCase().includes(q));
  }, [monitors, debouncedName]);

  // Counts — use API-provided statusCounts (total across ALL matching monitors, not just current page)
  // Amendment 2026-03-02: fixes dashboard showing page-level counts instead of actual totals
  const upCount = statusCounts.UP;
  const degradedCount = statusCounts.DEGRADED;
  const downCount = statusCounts.DOWN;

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-surface rounded-lg p-4 animate-pulse h-24" />
        ))}
      </div>
    );
  }

  return (
    <div>
      {/* Summary counters */}
      <div className="flex gap-4 mb-6 flex-wrap">
        <div className="flex items-center gap-2">
          <StatusBadge state="UP" size="lg" />
          <span className="text-text-primary text-h2 2xl:text-display">{upCount}</span>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge state="DEGRADED" size="lg" />
          <span className="text-text-primary text-h2 2xl:text-display">{degradedCount}</span>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge state="DOWN" size="lg" />
          <span className="text-text-primary text-h2 2xl:text-display">{downCount}</span>
        </div>
      </div>

      {/* Filters + Create */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <FilterBar
          filters={filters}
          onChange={setFilters}
          availableProviders={providers}
          availableAccounts={accounts}
          availableClients={clients}
        />
        {isAdmin && (
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="bg-brand hover:bg-brand-hover text-white font-medium rounded-lg px-4 py-2 min-h-[44px] transition-colors"
          >
            + New Monitor
          </button>
        )}
      </div>

      {error && (
        <div className="bg-status-down/20 text-status-down rounded-lg px-4 py-3 mb-4 text-sm">{error}</div>
      )}

      {/* Create form */}
      {showCreateForm && isAdmin && <CreateMonitorForm onCreated={() => { setShowCreateForm(false); fetchMonitors(); }} />}

      {/* Monitor grid */}
      {visibleMonitors.length === 0 ? (
        <div className="bg-surface rounded-lg p-12 text-center text-text-muted">
          {debouncedName
            ? 'No monitors match your search.'
            : `No monitors found.${isAdmin ? ' Create one to get started.' : ''}`
          }
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
            {visibleMonitors.map((m) => (
              <MonitorCard key={m.id} monitor={m} isAdmin={isAdmin} onToggleMute={handleToggleMute} />
            ))}
          </div>
          {!debouncedName && (
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages || Math.ceil(pagination.total / pagination.limit)}
              total={pagination.total}
              limit={pagination.limit}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
            />
          )}
        </>
      )}
    </div>
  );
}

function CreateMonitorForm({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [interval, setInterval] = useState(600);
  const [maxRetries, setMaxRetries] = useState(3);
  const [hostingProvider, setHostingProvider] = useState('');
  const [providerAccount, setProviderAccount] = useState('');
  const [client, setClient] = useState('');
  const [recipients, setRecipients] = useState<{ type: 'email' | 'sms'; address: string }[]>([]);
  const [headers, setHeaders] = useState<{ key: string; value: string }[]>([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // New recipient input
  const [recipientType, setRecipientType] = useState<'email' | 'sms'>('email');
  const [recipientAddress, setRecipientAddress] = useState('');

  const addRecipient = () => {
    if (!recipientAddress.trim()) return;
    setRecipients((prev) => [...prev, { type: recipientType, address: recipientAddress.trim() }]);
    setRecipientAddress('');
  };

  const removeRecipient = (index: number) => {
    setRecipients((prev) => prev.filter((_, i) => i !== index));
  };

  const addHeader = () => {
    setHeaders((prev) => [...prev, { key: '', value: '' }]);
  };

  const updateHeader = (index: number, field: 'key' | 'value', val: string) => {
    setHeaders((prev) => prev.map((h, i) => i === index ? { ...h, [field]: val } : h));
  };

  const removeHeader = (index: number) => {
    setHeaders((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const headersObj: Record<string, string> = {};
      headers.forEach((h) => { if (h.key.trim()) headersObj[h.key.trim()] = h.value; });

      await createMonitor({
        name, url, interval, maxRetries,
        recipients,
        headers: Object.keys(headersObj).length > 0 ? headersObj : undefined,
        hostingProvider: hostingProvider || undefined,
        providerAccount: providerAccount || undefined,
        client: client || undefined,
      });
      onCreated();
    } catch (err: any) {
      setError(err.message || 'Failed to create monitor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-surface rounded-lg p-4 mb-4 border border-border space-y-3">
      <h3 className="text-text-primary font-semibold">Create Monitor</h3>
      {error && <div className="text-status-down text-sm">{error}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input type="text" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)}
          className="w-full bg-bg-primary text-text-primary border border-border rounded px-3 py-2 text-sm" required />
        <input type="url" placeholder="https://example.com" value={url} onChange={(e) => setUrl(e.target.value)}
          className="w-full bg-bg-primary text-text-primary border border-border rounded px-3 py-2 text-sm" required />
        <div>
          <label className="text-text-muted text-xs">Interval (seconds)</label>
          <input type="number" value={interval} onChange={(e) => setInterval(Number(e.target.value))} min={30}
            className="w-full bg-bg-primary text-text-primary border border-border rounded px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-text-muted text-xs">Max Retries</label>
          <input type="number" value={maxRetries} onChange={(e) => setMaxRetries(Number(e.target.value))} min={0} max={10}
            className="w-full bg-bg-primary text-text-primary border border-border rounded px-3 py-2 text-sm" />
        </div>
      </div>

      {/* Advanced toggle */}
      <button type="button" onClick={() => setShowAdvanced(!showAdvanced)}
        className="text-brand text-sm hover:underline">
        {showAdvanced ? '▾ Hide advanced' : '▸ Show advanced (tags, headers, recipients)'}
      </button>

      {showAdvanced && (
        <div className="space-y-3 pl-2 border-l-2 border-border">
          {/* Tags */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-text-muted text-xs">Hosting Provider</label>
              <input type="text" placeholder="AWS, Azure, GCP..." value={hostingProvider}
                onChange={(e) => setHostingProvider(e.target.value)}
                className="w-full bg-bg-primary text-text-primary border border-border rounded px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-text-muted text-xs">Provider Account</label>
              <input type="text" placeholder="Account ID / subscription" value={providerAccount}
                onChange={(e) => setProviderAccount(e.target.value)}
                className="w-full bg-bg-primary text-text-primary border border-border rounded px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-text-muted text-xs">Client</label>
              <input type="text" placeholder="Defaults to domain" value={client}
                onChange={(e) => setClient(e.target.value)}
                className="w-full bg-bg-primary text-text-primary border border-border rounded px-3 py-2 text-sm" />
            </div>
          </div>

          {/* Recipients */}
          <div>
            <label className="text-text-muted text-xs block mb-1">Alert Recipients</label>
            {recipients.map((r, i) => (
              <div key={i} className="flex items-center gap-2 mb-1">
                <span className="text-text-muted text-xs w-12">{r.type}</span>
                <span className="text-text-secondary text-sm flex-1">{r.address}</span>
                <button type="button" onClick={() => removeRecipient(i)} className="text-status-down text-xs hover:underline">remove</button>
              </div>
            ))}
            <div className="flex gap-2 items-center mt-1">
              <select value={recipientType} onChange={(e) => setRecipientType(e.target.value as 'email' | 'sms')}
                className="bg-bg-primary text-text-primary border border-border rounded px-2 py-1 text-sm">
                <option value="email">Email</option>
                <option value="sms">SMS</option>
              </select>
              <input type="text" placeholder={recipientType === 'email' ? 'user@example.com' : '+1234567890'}
                value={recipientAddress} onChange={(e) => setRecipientAddress(e.target.value)}
                className="flex-1 bg-bg-primary text-text-primary border border-border rounded px-2 py-1 text-sm" />
              <button type="button" onClick={addRecipient} className="text-brand text-sm hover:underline">+ Add</button>
            </div>
          </div>

          {/* HTTP Headers */}
          <div>
            <label className="text-text-muted text-xs block mb-1">Custom HTTP Headers</label>
            {headers.map((h, i) => (
              <div key={i} className="flex gap-2 mb-1">
                <input type="text" placeholder="Header-Name" value={h.key}
                  onChange={(e) => updateHeader(i, 'key', e.target.value)}
                  className="flex-1 bg-bg-primary text-text-primary border border-border rounded px-2 py-1 text-sm" />
                <input type="text" placeholder="value" value={h.value}
                  onChange={(e) => updateHeader(i, 'value', e.target.value)}
                  className="flex-1 bg-bg-primary text-text-primary border border-border rounded px-2 py-1 text-sm" />
                <button type="button" onClick={() => removeHeader(i)} className="text-status-down text-xs hover:underline">×</button>
              </div>
            ))}
            <button type="button" onClick={addHeader} className="text-brand text-sm hover:underline">+ Add Header</button>
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <button type="submit" disabled={loading} className="bg-brand hover:bg-brand-hover text-white rounded px-4 py-2 text-sm min-h-[44px] disabled:opacity-50">
          {loading ? 'Creating...' : 'Create'}
        </button>
        <button type="button" onClick={onCreated} className="bg-surface-hover text-text-secondary rounded px-4 py-2 text-sm min-h-[44px]">Cancel</button>
      </div>
    </form>
  );
}
